import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Mental Map — FTUE demo (3 essais).
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DIRECTION_ANGLES, DIRECTION_LABEL, } from './params';
const DIRECTION_KEY = {
    0: 'n',
    45: 'ne',
    90: 'e',
    135: 'se',
    180: 's',
    225: 'sw',
    270: 'w',
    315: 'nw',
};
const DEMO_STEPS = [
    {
        idx: 1,
        titleKey: 'mental_map.demo.step1_title',
        hintKey: 'mental_map.demo.step1_hint',
        gridSize: 5,
        landmarks: [
            { id: 'a', token: '🌳', name: 'Arbre', row: 0, col: 0 },
            { id: 'b', token: '🏛', name: 'Temple', row: 0, col: 4 },
            { id: 'c', token: '🌊', name: 'Vague', row: 4, col: 2 },
        ],
        fromId: 'a',
        toId: 'b',
        expectedDirection: 90, // Est
    },
    {
        idx: 2,
        titleKey: 'mental_map.demo.step2_title',
        hintKey: 'mental_map.demo.step2_hint',
        gridSize: 5,
        landmarks: [
            { id: 'a', token: '⛪', name: 'Église', row: 4, col: 0 },
            { id: 'b', token: '🗼', name: 'Tour', row: 0, col: 4 },
        ],
        fromId: 'a',
        toId: 'b',
        expectedDirection: 45, // Nord-Est
    },
    {
        idx: 3,
        titleKey: 'mental_map.demo.step3_title',
        hintKey: 'mental_map.demo.step3_hint',
        gridSize: 5,
        landmarks: [
            { id: 'a', token: '🌳', name: 'Arbre', row: 0, col: 0 },
            { id: 'b', token: '🏛', name: 'Temple', row: 0, col: 4 },
            { id: 'c', token: '🌊', name: 'Vague', row: 4, col: 0 },
            { id: 'd', token: '⛰', name: 'Mont', row: 4, col: 4 },
        ],
        fromId: 'd',
        toId: 'a',
        expectedDirection: 315, // Nord-Ouest
    },
];
const FIELD = 240;
export function Demo({ level: _level, onComplete, onSkip }) {
    const { t } = useTranslation(['games', 'common']);
    const [stepIdx, setStepIdx] = useState(0);
    const [phase, setPhase] = useState('showing');
    const [pick, setPick] = useState(null);
    const timerRef = useRef(null);
    const step = DEMO_STEPS[stepIdx];
    const isLast = stepIdx === DEMO_STEPS.length - 1;
    const fromLm = step.landmarks.find((l) => l.id === step.fromId);
    const toLm = step.landmarks.find((l) => l.id === step.toId);
    useEffect(() => {
        setPhase('showing');
        setPick(null);
        timerRef.current = setTimeout(() => setPhase('awaiting'), 4000);
        return () => {
            if (timerRef.current)
                clearTimeout(timerRef.current);
        };
    }, [stepIdx]);
    const correct = (() => {
        if (pick === null)
            return false;
        const diff = Math.abs(pick - step.expectedDirection);
        const wrap = Math.min(diff, 360 - diff);
        return wrap <= 45;
    })();
    const handlePick = (d) => {
        if (phase !== 'awaiting')
            return;
        setPick(d);
        setPhase('feedback');
    };
    const handleNext = () => {
        if (isLast) {
            onComplete();
            return;
        }
        setStepIdx((i) => i + 1);
    };
    const cells = [];
    for (let r = 0; r < step.gridSize; r += 1) {
        for (let c = 0; c < step.gridSize; c += 1)
            cells.push({ row: r, col: c });
    }
    const lmAt = new Map();
    for (const lm of step.landmarks)
        lmAt.set(`${lm.row}-${lm.col}`, lm);
    const expectedDirectionLabel = t(`mental_map.directions.${DIRECTION_KEY[step.expectedDirection]}`);
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s4 py-s4 text-center", children: [_jsx("p", { className: "font-mono text-[9.5px] uppercase tracking-[0.30em] text-cyan-text-faint", children: t('common:demo.eyebrow_with_master', { master: t('mental_map.demo.master') }) }), _jsx("h2", { className: "font-display text-[22px] font-medium leading-7 tracking-cy-display text-cyan-text", children: t(step.titleKey) }), _jsx("p", { className: "max-w-[320px] text-[13.5px] text-cyan-text-secondary", children: t(step.hintKey) }), phase === 'showing' ? (_jsx("div", { className: "grid gap-s1 border border-cyan-border-faint bg-cyan-surface p-s3", style: {
                    width: FIELD,
                    height: FIELD,
                    gridTemplateColumns: `repeat(${step.gridSize}, minmax(0, 1fr))`,
                }, children: cells.map((c) => {
                    const lm = lmAt.get(`${c.row}-${c.col}`);
                    return (_jsx("div", { className: "flex aspect-square items-center justify-center bg-cyan-bg text-[16px]", children: lm ? lm.token : '' }, `${c.row}-${c.col}`));
                }) })) : (_jsxs(_Fragment, { children: [_jsx("p", { className: "text-[13px] text-cyan-text-secondary", children: t('mental_map.demo.question', {
                            toName: toLm.name,
                            toToken: toLm.token,
                            fromName: fromLm.name,
                            fromToken: fromLm.token,
                        }) }), _jsxs("div", { className: "relative h-[200px] w-[200px]", children: [_jsx("div", { className: "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 select-none font-display text-[12px] text-cyan-text-secondary", children: "?" }), DIRECTION_ANGLES.map((a) => {
                                const isCorrect = phase === 'feedback' && a === step.expectedDirection;
                                const isPicked = phase === 'feedback' && pick === a;
                                let cls = 'border border-cyan-border bg-cyan-surface text-cyan-text hover:bg-cyan-bg';
                                if (isCorrect)
                                    cls = 'border-2 border-cyan-core bg-cyan-core text-cyan-surface';
                                else if (isPicked)
                                    cls = 'border-2 border-cyan-media bg-cyan-media text-cyan-surface';
                                return (_jsx("button", { type: "button", onClick: () => handlePick(a), disabled: phase !== 'awaiting', "aria-label": DIRECTION_LABEL[a], className: [
                                        'absolute left-1/2 top-1/2 flex h-[40px] w-[40px] -translate-x-1/2 -translate-y-1/2',
                                        'items-center justify-center rounded-full font-mono text-[10px] transition',
                                        cls,
                                    ].join(' '), style: {
                                        transform: `translate(-50%, -50%) rotate(${a}deg) translateY(-80px) rotate(-${a}deg)`,
                                    }, children: DIRECTION_LABEL[a] }, a));
                            })] })] })), _jsxs("div", { className: "flex flex-col items-stretch gap-s3 self-stretch", children: [phase === 'feedback' && (_jsxs(_Fragment, { children: [_jsx("p", { className: [
                                    'text-[13px]',
                                    correct ? 'text-cyan-core' : 'text-cyan-media',
                                ].join(' '), children: correct
                                    ? t('mental_map.demo.feedback_correct', {
                                        name: toLm.name,
                                        direction: expectedDirectionLabel,
                                    })
                                    : t('mental_map.demo.feedback_incorrect', {
                                        direction: expectedDirectionLabel,
                                    }) }), _jsx("button", { type: "button", onClick: handleNext, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: isLast ? t('common:actions.start_session') : t('common:actions.next_trial') })] })), _jsx("button", { type: "button", onClick: onSkip, className: "text-[12px] text-cyan-text-secondary underline-offset-4 hover:underline", children: t('common:actions.skip_demo') })] })] }));
}
//# sourceMappingURL=demo.js.map